/* eslint-disable */
import { Any } from "../../google/protobuf/any";
import { Id } from "../common/id";
import { AuditLogProcessingWorkflow } from "./audit_log_processing_workflow";
import { CacheReconcileTaskAttemptWorkflow, CacheReconcileTaskWorkflow } from "./cache_reconcile_workflow";
import { CanaryWorkflow } from "./canary_workflow";
import { ConfigurationCheckWorkflowData } from "./configuration_check_workflow";
import { ConsumptionMetricsExporterWorkflow } from "./consumption_metrics_exporter_workflow";
import { DataValidationWorkflow } from "./data_validation_workflow";
import { DatabricksSetupWorkflow } from "./databricks_setup_workflow";
import { DatabricksValidationClusterWorkflow } from "./databricks_validation_cluster_workflow";
import { DynamoImportTableWorkflow } from "./dynamo_import_table_workflow";
import { EMRValidationClusterWorkflowProto } from "./emr_validation_cluster_workflow";
import { MaterializationSchedulingWorkflow } from "./materialization_scheduling_workflow";
import { PlanIntegrationTestStatusWorkflow } from "./plan_integration_test_status_workflow";
import { RayMaterializationWorkflow } from "./ray_materialization_workflow";
import { SelfServeConsumptionDeliveryWorkflow } from "./self_serve_consumption_delivery_workflow";
import { ServerGroupReconciliationWorkflow } from "./server_group_reconciliation_workflow";
import { ServerGroupStateMachineWorkflowData } from "./server_group_state_machine_workflow";
import { SparkExecutionWorkflow } from "./spark_execution_workflow";
import { TerminalStateOptions_WorkflowTerminationCode } from "./state_machine_workflow";
import { TestStateMachineWorkflowData } from "./test_state_machine_workflow";
import { TestWorkflow } from "./test_workflow";
import { VmMaterializationWorkflow } from "./vm_materialization_workflow";

export const protobufPackage = "tecton_proto.workflows";

export enum WorkflowType {
  /** UNKNOWN_WORKFLOW - note these names cannot be changed because the names are used as DB indices */
  UNKNOWN_WORKFLOW = "UNKNOWN_WORKFLOW",
  /** SPARK_EXECUTION_WORKFLOW - deprecated */
  SPARK_EXECUTION_WORKFLOW = "SPARK_EXECUTION_WORKFLOW",
  CANARY_WORKFLOW = "CANARY_WORKFLOW",
  DATABRICKS_VALIDATION_CLUSTER_WORKFLOW = "DATABRICKS_VALIDATION_CLUSTER_WORKFLOW",
  DATABRICKS_SETUP_WORKFLOW = "DATABRICKS_SETUP_WORKFLOW",
  /** JOB_EXECUTION_WORKFLOW - deprecated */
  JOB_EXECUTION_WORKFLOW = "JOB_EXECUTION_WORKFLOW",
  SPARK_EXECUTION_WORKFLOW_V2 = "SPARK_EXECUTION_WORKFLOW_V2",
  MATERIALIZATION_SCHEDULING_WORKFLOW = "MATERIALIZATION_SCHEDULING_WORKFLOW",
  DATA_VALIDATION_WORKFLOW = "DATA_VALIDATION_WORKFLOW",
  EMR_VALIDATION_CLUSTER_WORKFLOW = "EMR_VALIDATION_CLUSTER_WORKFLOW",
  AUDIT_LOG_PROCESING_WORKFLOW = "AUDIT_LOG_PROCESING_WORKFLOW",
  SELF_SERVE_CONSUMPTION_DELIVERY_WORKFLOW = "SELF_SERVE_CONSUMPTION_DELIVERY_WORKFLOW",
  DYNAMO_IMPORT_TABLE_WORKFLOW = "DYNAMO_IMPORT_TABLE_WORKFLOW",
  CONSUMPTION_METRICS_EXPORTER_WORKFLOW = "CONSUMPTION_METRICS_EXPORTER_WORKFLOW",
  /** RAY_MATERIALIZATION_WORKFLOW - Deprecated in favor of VM_MATERIALIZATION_WORKFLOW */
  RAY_MATERIALIZATION_WORKFLOW = "RAY_MATERIALIZATION_WORKFLOW",
  VM_MATERIALIZATION_WORKFLOW = "VM_MATERIALIZATION_WORKFLOW",
  PLAN_INTEGRATION_TEST_STATUS_WORKFLOW = "PLAN_INTEGRATION_TEST_STATUS_WORKFLOW",
  CACHE_RECONCILE_TASK_WORKFLOW = "CACHE_RECONCILE_TASK_WORKFLOW",
  CACHE_RECONCILE_TASK_ATTEMPT_WORKFLOW = "CACHE_RECONCILE_TASK_ATTEMPT_WORKFLOW",
  TEST_WORKFLOW = "TEST_WORKFLOW",
  /** SERVER_GROUP_RECONCILIATION_WORKFLOW - Workflows that reconcile the state of server groups. */
  SERVER_GROUP_RECONCILIATION_WORKFLOW = "SERVER_GROUP_RECONCILIATION_WORKFLOW",
  TEST_STATE_MACHINE_WORKFLOW = "TEST_STATE_MACHINE_WORKFLOW",
  SERVER_GROUP_STATE_MACHINE_WORKFLOW = "SERVER_GROUP_STATE_MACHINE_WORKFLOW",
  CONFIGURATION_CHECK_WORKFLOW = "CONFIGURATION_CHECK_WORKFLOW",
}

export interface KotlinException {
  name?: string | undefined;
  message?:
    | string
    | undefined;
  /** kotlin .stackTraceToString() */
  stack_trace_to_string?: string | undefined;
}

/** Next ID: 41 */
export interface WorkflowStateContainer {
  workflow_id?: Id | undefined;
  parent_workflow_id?: Id | undefined;
  workflow_type?:
    | WorkflowType
    | undefined;
  /**
   * Whether or not the workflow is in a terminal state.
   * Deprecated: use base_state == TERMINATED instead.
   *
   * @deprecated
   */
  is_complete?:
    | boolean
    | undefined;
  /**
   * whether the workflow has been requested to be cancelled
   * note this is a sort of asynchronous signal, and a workflow implementation
   * can decide whether to ignore it or not
   */
  cancellation_requested?:
    | boolean
    | undefined;
  /** The general lifecycle status of the workflow. */
  base_state?:
    | WorkflowStateContainer_BaseState
    | undefined;
  /** What sort of terminal state was reached by the workflow (expected to be absent when base_state is not TERMINATED). */
  termination_code?:
    | TerminalStateOptions_WorkflowTerminationCode
    | undefined;
  /** The key used along with `workflow_type` to define an uniqueness constraint. */
  uniqueness_key?:
    | string
    | undefined;
  /** Common fields */
  created_at?: string | undefined;
  updated_at?: string | undefined;
  completed_at?: string | undefined;
  spark_execution_workflow?: SparkExecutionWorkflow | undefined;
  canary_workflow?: CanaryWorkflow | undefined;
  databricks_validation_cluster_workflow?: DatabricksValidationClusterWorkflow | undefined;
  databricks_setup_workflow?: DatabricksSetupWorkflow | undefined;
  materialization_scheduling_workflow?: MaterializationSchedulingWorkflow | undefined;
  data_validation_workflow?: DataValidationWorkflow | undefined;
  emr_validation_cluster_workflow?: EMRValidationClusterWorkflowProto | undefined;
  audit_log_processing_workflow?: AuditLogProcessingWorkflow | undefined;
  self_serve_consumption_delivery_workflow?: SelfServeConsumptionDeliveryWorkflow | undefined;
  dynamo_import_table_workflow?: DynamoImportTableWorkflow | undefined;
  consumption_metrics_exporter_workflow?:
    | ConsumptionMetricsExporterWorkflow
    | undefined;
  /** Deprecated in favor of vm_materialization_workflow */
  ray_materialization_workflow?: RayMaterializationWorkflow | undefined;
  vm_materialization_workflow?: VmMaterializationWorkflow | undefined;
  plan_integration_test_status_workflow?: PlanIntegrationTestStatusWorkflow | undefined;
  cache_reconcile_task_workflow?: CacheReconcileTaskWorkflow | undefined;
  cache_reconcile_task_attempt_workflow?: CacheReconcileTaskAttemptWorkflow | undefined;
  test_workflow?: TestWorkflow | undefined;
  server_group_reconciliation_workflow?: ServerGroupReconciliationWorkflow | undefined;
  test_state_machine_workflow?: TestStateMachineWorkflowData | undefined;
  server_group_state_machine_workflow?: ServerGroupStateMachineWorkflowData | undefined;
  configuration_check_workflow?:
    | ConfigurationCheckWorkflowData
    | undefined;
  /** this value increments each time specific_workflow is updated */
  specific_workflow_serial?: string | undefined;
  last_exception?:
    | KotlinException
    | undefined;
  /** What attempt this is for this particular workflow. */
  attempt_index?:
    | number
    | undefined;
  /** The subcontainer (specific_workflow) this workflow was created with. */
  initial_subcontainer?: Any | undefined;
}

export enum WorkflowStateContainer_BaseState {
  BASE_STATE_UNSPECIFIED = "BASE_STATE_UNSPECIFIED",
  /** QUEUED - Starting state; work has been requested but nothing done yet. */
  QUEUED = "QUEUED",
  /** ACTIVE - Work is in progress. */
  ACTIVE = "ACTIVE",
  /** CANCELLING - Work is being cancelled. */
  CANCELLING = "CANCELLING",
  /** TERMINATED - Workflow is complete (see termination_status for more details). */
  TERMINATED = "TERMINATED",
}

/** An entry in the workflow history table. */
export interface WorkflowHistoryEntry {
  workflow_id?: Id | undefined;
  event_id?: number | undefined;
  event_time?: string | undefined;
  workflow_type?:
    | WorkflowType
    | undefined;
  /** States are string names of enum values. */
  from_state?: string | undefined;
  to_state?: string | undefined;
  reason?: string | undefined;
}
